import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  Pencil,
  Phone,
  Plus,
  Search,
  Shield,
  ShieldOff,
  Trash2,
  User as UserIcon,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { usersApi } from '@/api/endpoints/users';
import { ApiError } from '@/api/client';
import { cn, formatDateTime } from '@/lib/utils';
import type { User } from '@/api/types';

export function UsersPage() {
  const list = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list({ all: true }),
  });
  const [editing, setEditing] = useState<User | 'new' | null>(null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const data = list.data ?? [];
    if (!q) return data;
    return data.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone?.toLowerCase().includes(q),
    );
  }, [list.data, search]);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Usuários</h1>
          <p className="text-sm text-muted-foreground">
            Gerencie quem tem acesso ao sistema e suas permissões.
          </p>
        </div>
        <Button onClick={() => setEditing('new')} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Novo usuário
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome, email ou telefone"
          className="rounded-full bg-secondary pl-9"
        />
      </div>

      {list.isLoading ? (
        <p className="p-6 text-sm text-muted-foreground">Carregando usuários…</p>
      ) : list.error ? (
        <p className="p-6 text-sm text-destructive">
          Erro ao carregar usuários (precisa permissão de admin/manager).
        </p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <UserIcon className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Nenhum usuário encontrado.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((u) => (
            <UserCard key={u.id} user={u} onEdit={() => setEditing(u)} />
          ))}
        </div>
      )}

      {editing && (
        <UserEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function UserCard({ user, onEdit }: { user: User; onEdit: () => void }) {
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-purple-500 text-white">
              <UserIcon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold">{user.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onEdit}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
            aria-label="Editar"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5 text-[11px]">
          {user.administrator && (
            <span className="inline-flex items-center gap-1 rounded-full bg-status-blocked/10 px-2 py-0.5 font-semibold text-status-blocked">
              <Shield className="h-3 w-3" />
              Admin
            </span>
          )}
          {user.readonly && (
            <span className="inline-flex items-center gap-1 rounded-full bg-status-ready/10 px-2 py-0.5 font-semibold text-status-ready">
              <ShieldOff className="h-3 w-3" />
              Somente leitura
            </span>
          )}
          {user.disabled && (
            <span className="inline-flex items-center gap-1 rounded-full bg-status-off/15 px-2 py-0.5 font-semibold text-status-off">
              Desabilitado
            </span>
          )}
          {!user.administrator && !user.readonly && !user.disabled && (
            <span className="inline-flex items-center gap-1 rounded-full bg-status-running/10 px-2 py-0.5 font-semibold text-status-running">
              Padrão
            </span>
          )}
        </div>

        {user.phone && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Phone className="h-3 w-3" />
            {user.phone}
          </p>
        )}
        {user.expirationTime && (
          <p className="text-[11px] text-muted-foreground">
            Expira em {formatDateTime(user.expirationTime)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

interface FormState {
  name: string;
  email: string;
  phone: string;
  password: string;
  disabled: boolean;
  readonly: boolean;
  administrator: boolean;
  limitCommands: boolean;
  expirationTime: string;
}

function fromUser(user: User): FormState {
  return {
    name: user.name ?? '',
    email: user.email ?? '',
    phone: user.phone ?? '',
    password: '',
    disabled: user.disabled ?? false,
    readonly: user.readonly ?? false,
    administrator: user.administrator ?? false,
    limitCommands: user.limitCommands ?? false,
    expirationTime: user.expirationTime ? user.expirationTime.slice(0, 10) : '',
  };
}

function UserEditor({ initial, onClose }: { initial: User | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(
    initial
      ? fromUser(initial)
      : {
          name: '',
          email: '',
          phone: '',
          password: '',
          disabled: false,
          readonly: false,
          administrator: false,
          limitCommands: false,
          expirationTime: '',
        },
  );
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (initial) setForm(fromUser(initial));
  }, [initial]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const errors = useMemo(() => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) e.name = 'Obrigatório';
    if (!form.email.trim()) e.email = 'Obrigatório';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = 'E-mail inválido';
    if (!initial && !form.password) e.password = 'Senha obrigatória ao criar';
    return e;
  }, [form, initial]);

  const valid = Object.keys(errors).length === 0;

  const buildPayload = (): Partial<User> => {
    const payload: Partial<User> & Record<string, unknown> = {
      ...(initial ?? {}),
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim() || undefined,
      disabled: form.disabled,
      readonly: form.readonly,
      administrator: form.administrator,
      limitCommands: form.limitCommands,
      expirationTime: form.expirationTime
        ? new Date(form.expirationTime).toISOString()
        : undefined,
    };
    if (form.password) payload.password = form.password;
    return payload;
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = buildPayload();
      if (initial) return usersApi.update(initial.id, payload as User);
      return usersApi.create(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => usersApi.delete(initial!.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  const errorText = (e: unknown) => {
    if (e instanceof ApiError) return `Servidor respondeu ${e.status} (${e.statusText || 'erro'}).`;
    if (e instanceof Error) return e.message;
    return 'Erro desconhecido.';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    saveMutation.mutate();
  };

  const onConfirmDelete = () => {
    if (!initial) return;
    if (!window.confirm(`Excluir o usuário "${initial.name}"? Essa ação não pode ser desfeita.`)) return;
    deleteMutation.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-2 sm:p-6"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="flex max-h-[95vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-bold">
            {initial ? 'Editar usuário' : 'Novo usuário'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <Field label="Nome" required error={touched ? errors.name : undefined}>
            <Input value={form.name} onChange={(e) => update('name', e.target.value)} />
          </Field>
          <Field label="E-mail" required error={touched ? errors.email : undefined}>
            <Input
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
            />
          </Field>
          <Field
            label={initial ? 'Nova senha (opcional)' : 'Senha'}
            required={!initial}
            error={touched ? errors.password : undefined}
          >
            <Input
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => update('password', e.target.value)}
              placeholder={initial ? 'Deixe em branco para manter a atual' : ''}
            />
          </Field>
          <Field label="Telefone">
            <Input value={form.phone} onChange={(e) => update('phone', e.target.value)} />
          </Field>
          <Field label="Expira em">
            <Input
              type="date"
              value={form.expirationTime}
              onChange={(e) => update('expirationTime', e.target.value)}
            />
          </Field>

          <div className="space-y-2 rounded-md border border-border p-3">
            <p className="text-xs font-semibold text-muted-foreground">Permissões</p>
            <Toggle
              label="Administrador"
              description="Acesso total ao sistema"
              checked={form.administrator}
              onChange={(v) => update('administrator', v)}
            />
            <Toggle
              label="Somente leitura"
              description="Não pode editar nada"
              checked={form.readonly}
              onChange={(v) => update('readonly', v)}
            />
            <Toggle
              label="Bloquear envio de comandos"
              description="Não pode enviar comandos pra máquinas"
              checked={form.limitCommands}
              onChange={(v) => update('limitCommands', v)}
            />
            <Toggle
              label="Desabilitado"
              description="Usuário não consegue fazer login"
              checked={form.disabled}
              onChange={(v) => update('disabled', v)}
            />
          </div>

          {(saveMutation.error || deleteMutation.error) && (
            <p className="flex items-start gap-2 rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {errorText(saveMutation.error ?? deleteMutation.error)}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
          {initial && (
            <Button
              type="button"
              variant="destructive"
              onClick={onConfirmDelete}
              disabled={deleteMutation.isPending || saveMutation.isPending}
              className="mr-auto gap-1.5"
            >
              <Trash2 className="h-4 w-4" />
              {deleteMutation.isPending ? 'Excluindo…' : 'Excluir'}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!valid || saveMutation.isPending} className="gap-1.5">
            <Check className="h-4 w-4" />
            {saveMutation.isPending ? 'Salvando…' : initial ? 'Salvar' : 'Criar'}
          </Button>
        </footer>
      </form>
    </div>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
        <span>
          {label}
          {required && <span className="text-status-blocked"> *</span>}
        </span>
        {error && <span className="text-status-blocked">{error}</span>}
      </span>
      <div className={cn(error && 'rounded-md ring-2 ring-status-blocked/30')}>{children}</div>
    </label>
  );
}

function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 cursor-pointer rounded border-border accent-primary"
      />
      <span className="flex-1">
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-[11px] text-muted-foreground">{description}</span>
      </span>
    </label>
  );
}
